import { get, post, put, patch, destroy } from '@rails/request.js'

/**
 * All Fireside web data flows through the engine JSON API under `/api/v1`
 * (build-contract §3). The client holds NO recommendation logic and NEVER
 * calls Claude — it only gathers session context and renders grounded results.
 */
const API_BASE = '/api/v1'

class ApiError extends Error {
  constructor(
    public status: number,
    public body: Record<string, unknown>,
  ) {
    const errObj = body.error as { message?: string } | string | undefined
    const message =
      typeof errObj === 'string' ? errObj : errObj?.message || `API error: ${status}`
    super(message)
    this.name = 'ApiError'
  }

  /** The contract error code (`{ error: { code, message } }`), when present. */
  get code(): string | undefined {
    const errObj = this.body.error as { code?: string } | undefined
    return errObj?.code
  }
}

function withBase(path: string): string {
  if (/^https?:\/\//.test(path)) return path
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`
}

async function request<T>(method: typeof get, path: string, body?: unknown): Promise<T> {
  const response = await method(withBase(path), {
    body: body ?? undefined,
    responseKind: 'json',
  })

  if (response.statusCode === 204) return undefined as T

  if (!response.ok) {
    let errorBody: Record<string, unknown> = {}
    try {
      errorBody = (await response.json) as Record<string, unknown>
    } catch {
      // Server returned non-JSON (e.g. an HTML error page).
    }
    throw new ApiError(response.statusCode, errorBody)
  }

  try {
    return (await response.json) as T
  } catch {
    // A 2xx with a non-JSON body (a proxy page, an HTML error saved by a CDN)
    // is still a broken contract — surface it as the same typed error the
    // failure path throws, not a bare Error from the fetch wrapper.
    throw new ApiError(response.statusCode, { error: 'Expected a JSON response' })
  }
}

export const api = {
  get: <T>(path: string) => request<T>(get, path),
  post: <T>(path: string, body?: unknown) => request<T>(post, path, body),
  patch: <T>(path: string, body?: unknown) => request<T>(patch, path, body),
  put: <T>(path: string, body?: unknown) => request<T>(put, path, body),
  delete: <T>(path: string) => request<T>(destroy, path),
}

/** Absolute URL for a server-side redirect flow (Steam OpenID sign-in). */
export function apiUrl(path: string): string {
  return withBase(path)
}

export { ApiError }
